import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  alpha,
  keyframes,
  useTheme,
} from '@mui/material';
import { Language as LanguageIcon } from '@mui/icons-material';
import type { DashboardBotRow } from '../../hooks/useDashboardBots';

type SortKey = 'provider' | 'region' | 'public_ip' | 'public_ipv6' | 'status' | 'heartbeat';
type SortDir = 'asc' | 'desc';

const COLUMNS: Array<{ key: SortKey; label: string }> = [
  { key: 'provider', label: 'Provider' },
  { key: 'region', label: 'Region' },
  { key: 'public_ip', label: 'Public IP' },
  { key: 'public_ipv6', label: 'Public IPv6' },
  { key: 'status', label: 'Status' },
  { key: 'heartbeat', label: 'Heartbeat' },
];

// Comparator for a single column. Heartbeat sorts by telemetry freshness
// (never-seen rows sort last); every other column is a natural string compare
// so IPs and regions order the way a human reads them (2 before 10).
const compareRows = (a: DashboardBotRow, b: DashboardBotRow, key: SortKey): number => {
  if (key === 'heartbeat') {
    const av = a.lastSeenMs ?? -Infinity;
    const bv = b.lastSeenMs ?? -Infinity;
    return av - bv;
  }
  const av = (a[key] ?? '') as string;
  const bv = (b[key] ?? '') as string;
  return av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
};

const heartbeatPulse = keyframes`
  0% { transform: scale(0.9); opacity: 0.6; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(0.9); opacity: 0.6; }
`;

// Short "time since" label for the heartbeat column.
const agoLabel = (lastSeenMs: number | null): string => {
  if (lastSeenMs == null) return 'no signal';
  const secs = Math.max(0, Math.round((Date.now() - lastSeenMs) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
};

// Muted color hint for the DB status column.
const statusColor = (theme: any, status: string): string => {
  const s = (status || '').toLowerCase();
  if (s === 'running') return theme.palette.success.main;
  if (s === 'error') return theme.palette.error.main;
  if (['pending', 'creating', 'launching', 'starting'].includes(s)) return theme.palette.warning.main;
  if (['terminating', 'shutting-down', 'shutting_down', 'stopping'].includes(s)) return theme.palette.warning.main;
  return theme.palette.text.secondary;
};

type BotsIpRegionTableProps = {
  rows: DashboardBotRow[];
};

export const BotsIpRegionTable = React.memo(({ rows }: BotsIpRegionTableProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const containerRef = useRef<HTMLDivElement | null>(null);
  const headerRowRef = useRef<HTMLTableRowElement | null>(null);
  const rafPendingRef = useRef(false);
  const latestScrollTopRef = useRef(0);

  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(420);
  const [headerHeight, setHeaderHeight] = useState(36);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null);

  const ROW_HEIGHT = 36; // small table row height
  const OVERSCAN = 12;

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const update = () => setContainerHeight(el.clientHeight || 420);
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = headerRowRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const update = () => setHeaderHeight(el.getBoundingClientRect().height || 36);
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    latestScrollTopRef.current = e.currentTarget.scrollTop;
    if (rafPendingRef.current) return;
    rafPendingRef.current = true;
    requestAnimationFrame(() => {
      rafPendingRef.current = false;
      setScrollTop(latestScrollTopRef.current);
    });
  };

  // Rows in display order: original DB order until the user picks a sort column.
  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const dirMul = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => compareRows(a, b, sort.key) * dirMul);
  }, [rows, sort]);

  const handleSort = (key: SortKey) => {
    setSort((prev) =>
      prev?.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };

  const { visibleRows, topSpacerPx, bottomSpacerPx } = useMemo(() => {
    if (!sortedRows || sortedRows.length === 0) {
      return { visibleRows: [] as DashboardBotRow[], topSpacerPx: 0, bottomSpacerPx: 0 };
    }

    const bodyViewportHeight = Math.max(0, containerHeight - headerHeight);
    const bodyScrollTop = Math.max(0, scrollTop - headerHeight);

    const startIndex = Math.max(0, Math.floor(bodyScrollTop / ROW_HEIGHT) - OVERSCAN);
    const visibleCount = Math.ceil(bodyViewportHeight / ROW_HEIGHT) + OVERSCAN * 2;
    const endIndex = Math.min(sortedRows.length, startIndex + visibleCount);

    return {
      visibleRows: sortedRows.slice(startIndex, endIndex),
      topSpacerPx: startIndex * ROW_HEIGHT,
      bottomSpacerPx: Math.max(0, (sortedRows.length - endIndex) * ROW_HEIGHT),
    };
  }, [sortedRows, containerHeight, headerHeight, scrollTop]);

  const reportingCount = rows.reduce((n, r) => n + (r.reporting ? 1 : 0), 0);

  return (
    <Card
      sx={{
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(12px)',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.9)}, transparent)`,
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.18),
              borderRadius: '10px',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.3)}`,
            }}
          >
            <LanguageIcon sx={{ color: theme.palette.primary.main, fontSize: '1.15rem' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.75rem',
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              Bots-IP / Region
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
                color: theme.palette.text.secondary,
                mt: 0.25,
              }}
            >
              {rows.length} bots • {reportingCount} reporting
            </Typography>
          </Box>
        </Box>

        <TableContainer
          ref={containerRef}
          onScroll={handleScroll}
          sx={{
            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            borderRadius: 2,
            overflow: 'auto',
            maxHeight: { xs: 360, md: 520 },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow ref={headerRowRef}>
                {COLUMNS.map((col) => {
                  const active = sort?.key === col.key;
                  return (
                    <TableCell
                      key={col.key}
                      sortDirection={active ? sort!.dir : false}
                      sx={{
                        fontFamily: '"Space Mono", monospace',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: theme.palette.text.secondary,
                        backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.85 : 0.95),
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle',
                      }}
                    >
                      <TableSortLabel
                        active={active}
                        direction={active ? sort!.dir : 'asc'}
                        onClick={() => handleSort(col.key)}
                        sx={{
                          color: 'inherit !important',
                          '& .MuiTableSortLabel-icon': {
                            color: `${theme.palette.primary.main} !important`,
                            fontSize: '1rem',
                          },
                        }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      color: theme.palette.text.secondary,
                      borderBottom: 'none',
                      py: 3,
                    }}
                  >
                    No bots found for the selected scenario.
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {topSpacerPx > 0 && (
                    <TableRow hover={false} sx={{ height: `${topSpacerPx}px` }}>
                      <TableCell colSpan={6} sx={{ p: 0, borderBottom: 'none' }} />
                    </TableRow>
                  )}

                  {visibleRows.map((row, idx) => (
                    <TableRow
                      key={row.id || `${row.provider}-${row.region}-${row.public_ip}-${idx}`}
                      hover
                      sx={{
                        height: `${ROW_HEIGHT}px`,
                        '& td': {
                          borderBottom: `1px solid ${alpha(theme.palette.divider, isDark ? 0.3 : 0.6)}`,
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.75rem',
                          verticalAlign: 'middle',
                          py: 0.75,
                        },
                        '&:hover td': {
                          backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.04 : 0.08),
                        },
                      }}
                    >
                      <TableCell sx={{ color: theme.palette.primary.main }}>{row.provider || '—'}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{row.region || '—'}</TableCell>
                      <TableCell sx={{ color: 'text.primary' }}>{row.public_ip || '—'}</TableCell>
                      <TableCell sx={{ color: 'text.primary' }}>{row.public_ipv6 || '—'}</TableCell>
                      <TableCell sx={{ color: statusColor(theme, row.status), textTransform: 'lowercase' }}>
                        {row.status || '—'}
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          arrow
                          placement="left"
                          title={row.reporting ? 'reporting' : agoLabel(row.lastSeenMs)}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              flexShrink: 0,
                              cursor: 'default',
                              backgroundColor: row.reporting
                                ? theme.palette.success.main
                                : alpha(theme.palette.text.disabled, 0.6),
                              boxShadow: row.reporting
                                ? `0 0 6px ${alpha(theme.palette.success.main, 0.9)}`
                                : 'none',
                              animation: row.reporting ? `${heartbeatPulse} 1.2s ease-in-out infinite` : 'none',
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                  {bottomSpacerPx > 0 && (
                    <TableRow hover={false} sx={{ height: `${bottomSpacerPx}px` }}>
                      <TableCell colSpan={6} sx={{ p: 0, borderBottom: 'none' }} />
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
});

BotsIpRegionTable.displayName = 'BotsIpRegionTable';

